import * as React from "react";

import { cn } from "@/src/js/lib/utils";
import { DropdownSelect } from "../../../ui/dropdown-select";
import { Period, RollingPeriod } from "../../periods";
import { toDropdownOption } from "./periodChoices";

// Reporting years beyond the newest few are in the "Older reporting years" dropdown
const PRESET_REPORTING_YEAR_COUNT = 3;
const CUSTOM_RANGE_LABEL = "Custom Range…";

// Preflight is off, so a plain button keeps the browser's own border, padding and shadow. These are
// inline so no stylesheet rule for buttons can bring them back
const BARE_BUTTON_STYLE: React.CSSProperties = {
  appearance: "none",
  border: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
};

const PRESET_ROW_CLASS =
  "tw:w-full tw:cursor-pointer tw:rounded-md tw:px-3 tw:py-2 tw:text-left tw:text-sm tw:outline-none tw:hover:bg-neutral-100 tw:focus-visible:ring-[3px] tw:focus-visible:ring-neutral-950/50";
const SELECTED_PRESET_ROW_CLASS = "tw:bg-blue-50 tw:font-semibold tw:text-[#17436d]";

interface PresetsListProps {
  // Every reporting year, newest first
  reportingYears: Period[];
  rollingPeriods: RollingPeriod[];
  focusPeriod: Period;
  // Whether the custom range's calendar shows, which makes "Custom Range…" the choice instead of
  // any preset
  isCalendarShown: boolean;
  onSelectPreset: (period: Period) => void;
  onStartCustomRange: () => void;
}

// The periods to pick the focus from: the newest reporting years and the rolling periods as buttons,
// the older reporting years in a dropdown, and a link to choose a custom range of days
export function PresetsList({
  reportingYears,
  rollingPeriods,
  focusPeriod,
  isCalendarShown,
  onSelectPreset,
  onStartCustomRange,
}: PresetsListProps) {
  const presetPeriods = [
    ...reportingYears.slice(0, PRESET_REPORTING_YEAR_COUNT),
    ...rollingPeriods,
  ];
  const olderReportingYears = reportingYears.slice(PRESET_REPORTING_YEAR_COUNT);

  return (
    <fieldset className="tw:m-0 tw:min-w-0 tw:border-0 tw:border-b tw:border-neutral-200 tw:p-0 tw:sm:w-56 tw:sm:shrink-0 tw:sm:border-r tw:sm:border-b-0">
      <legend className="tw:mb-2 tw:px-4 tw:pt-4 tw:pb-0 tw:text-xs tw:font-medium tw:tracking-wider tw:text-neutral-500 tw:uppercase">
        Presets
      </legend>
      <div className="tw:grid tw:grid-cols-[minmax(0,1fr)] tw:content-start tw:gap-2 tw:px-4 tw:pb-4">
        <ul className="tw:m-0 tw:grid tw:list-none tw:gap-0.5 tw:p-0">
          {presetPeriods.map((period) => {
            const isSelected = period.key === focusPeriod.key && !isCalendarShown;
            return (
              <li key={period.key}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  style={BARE_BUTTON_STYLE}
                  className={cn(PRESET_ROW_CLASS, isSelected && SELECTED_PRESET_ROW_CLASS)}
                  onClick={() => onSelectPreset(period)}
                >
                  {period.label}
                </button>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              aria-pressed={isCalendarShown}
              style={BARE_BUTTON_STYLE}
              className={cn(
                PRESET_ROW_CLASS,
                "tw:font-medium tw:text-[#2f6fb3]",
                isCalendarShown && "tw:bg-blue-50",
              )}
              onClick={onStartCustomRange}
            >
              {CUSTOM_RANGE_LABEL}
            </button>
          </li>
        </ul>
        {olderReportingYears.length > 0 && (
          <DropdownSelect
            value={
              !isCalendarShown &&
              olderReportingYears.some((period) => period.key === focusPeriod.key)
                ? focusPeriod.key
                : ""
            }
            onValueChange={(key) =>
              onSelectPreset(
                olderReportingYears.find((period) => period.key === key) ?? focusPeriod,
              )
            }
            options={olderReportingYears.map((period) => toDropdownOption(period))}
            placeholder="Older reporting years"
            triggerClassName="tw:w-full"
            showTriggerDetail={false}
          />
        )}
      </div>
    </fieldset>
  );
}
